/**
 * Claim-free credit — the ONE implementation, shared by the rater and the documents (#34635).
 *
 * This used to live only in `packages/clarion-door-rating/src/shared/farm-helpers.ts`, while the
 * farm dec re-derived the same idea from `quote.homeClaimDates` — a field the farm rater never
 * reads and migrated policies never carry. The two answers therefore disagreed on every migrated
 * Forreston policy: the rater docked credit for claims the dec knew nothing about, so the dec
 * printed "Claim Free Discount – Included" on all 756 imported farm policies regardless. Hosting
 * the computation here, next to the loss-history projection it consumes, means the rater, the dec
 * and the staff UI cannot drift again — they all call this.
 *
 * The rules (Forreston's filed Claim Free Discount rule, mirrored by the CD `num_years_claim_free`
 * input, issue #30920):
 *   - `override === false` → 0 (UW forces no discount)
 *   - `override === true`  → 5 (UW forces full discount)
 *   - otherwise → years since the most recent COUNTED loss, capped at 5.
 *
 * Which losses count is `toRatingClaims`' business: non-ignored entries with a positive paid
 * amount. Nothing else is exempt — in particular a weather loss counts like any other, because a
 * claim in the migrated book is one the carrier itself paid.
 *
 * Legacy pulled `homeClaimsForUW` claims are UNIONed in rather than treated as an alternative: on
 * a policy that still carries them, adding one manual entry must not silently drop the pulled
 * claims and hand out undeserved credit. The result is a min over years-since-loss, so a claim
 * appearing in both lists is idempotent.
 */
import { calculateAge, getDateFromString, normalizeDate } from '@ourbranch/date-fns-helpers';
import { toRatingClaims, type RatingClaim } from './rating';
import type { LossHistoryEntryForRating } from './types';

/** Full claim-free credit: five clean years. Also the value a UW `true` override forces. */
export const MAX_CLAIM_FREE_YEARS = 5;

/** A loss this many years back or older no longer counts against claim-free credit. */
export const CLAIM_LOOKBACK_YEARS = 5;

/** The filed discount tiers. There is nothing between 0 and 3 — see getClaimFreeTier. */
export type ClaimFreeTier = 0 | 3 | 5;

export interface ClaimFreeInput {
  /** The canonical, segment-level loss history. */
  lossHistory?: LossHistoryEntryForRating[] | null;
  /** Legacy pre-#33939 pulled claims, unioned with `lossHistory`. */
  homeClaimsForUW?: RatingClaim[] | null;
  /** `global.claimFreeDiscount` — the UW override. `null`/`undefined` means auto-derive. */
  override?: boolean | null;
  /** Policy effective date (or rate-control date) the lookback is measured back from. */
  referenceDate?: string | null;
  /**
   * The value frozen at the term's inception (`global.numYearsClaimFreeAtInception`, #34641). When
   * present and no UW override is in force, it is returned verbatim instead of re-deriving from
   * claims, so a loss added mid-term does not drop the discount until the next renewal. `null`/
   * `undefined` means "no snapshot" (new business, or a legacy term that predates the field) — fall
   * through to live derivation. A frozen 0 is a real snapshot and is honored.
   */
  frozenYears?: number | null;
}

/**
 * Years since the most recent counted loss, capped at 5 — the `num_years_claim_free` rater input.
 *
 * Precedence: the UW override wins (it is a deliberate act), then the inception snapshot
 * (`frozenYears`, #34641), then live derivation from the claims. The snapshot freezes only the
 * claim-derived value, so the override still applies live on top of a frozen term.
 */
export function getClaimFreeYears({
  lossHistory,
  homeClaimsForUW,
  override,
  referenceDate,
  frozenYears
}: ClaimFreeInput): number {
  if (override === false) {
    return 0;
  }
  if (override === true) {
    return MAX_CLAIM_FREE_YEARS;
  }
  if (frozenYears != null) {
    return frozenYears;
  }

  const claims: RatingClaim[] = [...(homeClaimsForUW ?? []), ...toRatingClaims(lossHistory)];
  if (!referenceDate || !claims.length) {
    return MAX_CLAIM_FREE_YEARS;
  }

  const refDate = getDateFromString(normalizeDate(String(referenceDate)) as string, 'yyyy-MM-dd');
  let yearsSinceLastClaim = MAX_CLAIM_FREE_YEARS;

  for (const claim of claims) {
    if (!claim?.lossDate) {
      continue;
    }
    // Claims data (Verisk/A-Plus pull) supplies lossDate as `yyyyMMdd`, so normalize to
    // `yyyy-MM-dd` before the strict parse. Without this the parse yields an Invalid Date and
    // crashes the entire rate (#cfm-farm-rate-invalid-date).
    const lossDate = getDateFromString(normalizeDate(String(claim.lossDate)) as string, 'yyyy-MM-dd');
    if (Number.isNaN(lossDate.getTime())) {
      // Don't silently drop the claim (that would grant undeserved claim-free credit) — surface
      // the bad value so the upstream source can be fixed.
      console.warn(`getClaimFreeYears: unparseable lossDate ${JSON.stringify(claim.lossDate)}; skipping claim`);
      continue;
    }
    const years = calculateAge(lossDate, refDate).years;
    if (years < 0 || years >= CLAIM_LOOKBACK_YEARS) {
      continue;
    }
    if (years < yearsSinceLastClaim) {
      yearsSinceLastClaim = years;
    }
  }

  return yearsSinceLastClaim;
}

/**
 * Bucket a years-clean count into the filed tiers: 5 clean years earns the 5-year discount, 3 or 4
 * earns the 3-year discount, anything less earns nothing. Rating passes the raw year count to
 * Clarion Door and lets its factor table do this; the dec and the staff UI need the bucket itself,
 * to answer "is a claim-free discount actually being applied?" — a policy whose last loss was two
 * years ago has a non-zero year count but receives no credit, so `years > 0` is the wrong question.
 */
export function getClaimFreeTier(years: number): ClaimFreeTier {
  if (years >= MAX_CLAIM_FREE_YEARS) {
    return 5;
  }
  return years >= 3 ? 3 : 0;
}

/** Whether any claim-free credit applies at all — the question the dec and staff UI ask. */
export function hasClaimFreeCredit(input: ClaimFreeInput): boolean {
  return getClaimFreeTier(getClaimFreeYears(input)) > 0;
}

/**
 * Decide the value to persist as `global.numYearsClaimFreeAtInception` on a given rate (#34641).
 *
 * The claim-free discount is frozen at the term's inception: it is (re)derived on new business and
 * on each renewal, but a mid-term endorsement keeps the value the term was bound at. Post-data
 * rules — the one layer that knows whether this rate is a renewal (`isRenewal`) versus a mid-term
 * endorsement (`inPolicyMod && !isRenewal`) — call this each rate. A term already in force before
 * this field existed carries no snapshot, so its first mid-term rate lazily seeds one from the
 * current claims (`derivedYears`) rather than leaving the discount to drift.
 */
export function resolveClaimFreeYearsAtInception({
  derivedYears,
  frozenYears,
  isMidTermEndorsement
}: {
  /** What the claims-derived count is on this rate (the carrier supplies its own derivation). */
  derivedYears: number;
  /** The snapshot already on the record, if any. A stored 0 is real; only null/undefined is "unset". */
  frozenYears?: number | null;
  /** True when this rate is a mid-term endorsement (`inPolicyMod && !isRenewal`). */
  isMidTermEndorsement: boolean;
}): number {
  if (isMidTermEndorsement && frozenYears != null) {
    return frozenYears;
  }
  return derivedYears;
}

/**
 * The farm/home post-data-rule seam (#34641): given a policy's claims, the current snapshot, and
 * the rate context, return the value to store as `global.numYearsClaimFreeAtInception`.
 *
 * It derives the claim-based count with the UW override deliberately left OUT (`override: null`),
 * so only the claim-derived portion is frozen — the override is applied live by the rater at read
 * time, keeping the staff Discounts card's add/remove effective mid-term. The keep-vs-recompute
 * decision is `resolveClaimFreeYearsAtInception`.
 */
export function freezeFarmClaimFreeYears({
  lossHistory,
  homeClaimsForUW,
  referenceDate,
  frozenYears,
  inPolicyMod,
  isRenewal
}: {
  lossHistory?: LossHistoryEntryForRating[] | null;
  homeClaimsForUW?: RatingClaim[] | null;
  referenceDate?: string | null;
  frozenYears?: number | null;
  inPolicyMod?: boolean | null;
  isRenewal?: boolean | null;
}): number {
  const derivedYears = getClaimFreeYears({ lossHistory, homeClaimsForUW, override: null, referenceDate });
  return resolveClaimFreeYearsAtInception({
    derivedYears,
    frozenYears,
    isMidTermEndorsement: Boolean(inPolicyMod && !isRenewal)
  });
}

/** A claim as the Oakwood/home claim-free path consumes it (list + reference date form). */
export type ClaimForClaimFree = {
  lossDate?: string | null;
  amount?: number | null;
};

export type ClaimFreeOptions = {
  /** Maximum years of claim-free credit. Defaults to 5. */
  cap?: number;
  /** When true, claims with falsy or non-positive `amount` are skipped. */
  requirePositiveAmount?: boolean;
};

/**
 * Years since the most recent qualifying claim, capped at `options.cap` (default 5). The list form
 * used by the Oakwood home rater (HO_AAIS), which sources a pre-pulled `homeClaimsForUW` list and a
 * reference date directly rather than the `lossHistory` projection. Returns the cap when `claims`
 * is empty, no claim passes the filters, or `referenceDate` is missing. Carrier-specific UW
 * overrides are applied by the caller. Moved here from `clarion-door-rating` (#34641) so post-data
 * rules can share it.
 */
export function computeClaimFreeYears(
  claims: ReadonlyArray<ClaimForClaimFree>,
  referenceDate: string | null | undefined,
  options: ClaimFreeOptions = {}
): number {
  const cap = options.cap ?? 5;
  if (!referenceDate) {
    return cap;
  }

  let yearsSinceLastClaim = cap;
  for (const claim of claims) {
    if (!claim?.lossDate) {
      continue;
    }
    if (options.requirePositiveAmount && !(claim.amount && claim.amount > 0)) {
      continue;
    }
    const yearsSinceClaim = calculateAge(
      getDateFromString(claim.lossDate, 'yyyy-MM-dd'),
      getDateFromString(referenceDate, 'yyyy-MM-dd')
    ).years;
    yearsSinceLastClaim = Math.min(yearsSinceClaim, yearsSinceLastClaim);
  }

  return Math.max(0, yearsSinceLastClaim);
}

/**
 * The Clarion Door coverage line carrying the claim-free discount's dollar value. CD returns one
 * per rated risk; `farmCoverages` on an offer option (and `coverages` on a policy segment) are a
 * verbatim pass-through of its response, so this string is CD's, not ours.
 */
export const CLAIM_FREE_SAVINGS_COVERAGE = 'claim_free_savings';

/** A rated coverage line as Clarion Door returns it. */
interface RatedCoverage {
  type?: string | null;
  amount?: number | null;
}

/** Total claim-free dollars Clarion Door actually applied, summed across every rated risk. */
export function sumClaimFreeSavings(coverages: RatedCoverage[] | null | undefined): number {
  return (coverages || []).reduce((total, coverage) => {
    if (!coverage || coverage.type !== CLAIM_FREE_SAVINGS_COVERAGE) {
      return total;
    }
    const amount = Number(coverage.amount);
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);
}

/**
 * Whether a claim-free discount was actually APPLIED — the question the declaration and the staff
 * Discounts card ask. Distinct from `hasClaimFreeCredit`, which answers whether the insured is
 * claim-free: the two diverge when Clarion Door's factor yields nothing, and on liability-only
 * policies, where CD returns no property node and therefore no savings line at all (claim-free is
 * a property discount, so absent means "does not apply"). Owner decision, #34635 follow-up.
 *
 * The test is the SUM across risks, not any single line: a per-line `amount > 0` would call the
 * discount applied even if an offsetting negative left the insured no better off.
 */
export function hasAppliedClaimFreeSavings(coverages: RatedCoverage[] | null | undefined): boolean {
  return sumClaimFreeSavings(coverages) > 0;
}
